import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { AdminPanelComponent } from './components/admin-panel.component';
import { PatientViewComponent } from './components/patient-view.component';
import { ToastComponent } from './components/toast.component';
import { LoginComponent } from './components/login.component';
import { ContextService } from './services/context.service';
import { DatabaseService } from './services/db.service';
import { LoadingService } from './services/loading.service';
import { LoadingOverlayComponent } from './components/ui/loading-overlay.component';
import { MunicipalityPanelComponent } from './components/municipality-panel.component';
import { ProfileTabComponent } from './components/profile-tab.component';
import { PublicSearchComponent } from './components/public-search.component';
import { LEGAL_TEXTS } from './constants/legal-content';
import { AcsDashboardComponent } from './components/acs-dashboard.component';
import { ThemeService } from './services/theme.service';
import { BackgroundAuraComponent } from './components/ui/background-aura.component';
import { environment } from './environments/environment';
import { OperatorPanelComponent } from './components/operator-panel.component';
import { SessionTimeoutModalComponent } from './components/ui/session-timeout-modal.component';


@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'app-root',
   standalone: true,
   imports: [
      CommonModule,
      AdminPanelComponent,
      MunicipalityPanelComponent,
      PatientViewComponent,
      ToastComponent,
      LoginComponent,
      LoadingOverlayComponent,
      ProfileTabComponent,
      PublicSearchComponent,
      BackgroundAuraComponent,
      AcsDashboardComponent,
      OperatorPanelComponent,
      SessionTimeoutModalComponent,
   ],
   template: `
<div class="font-sans text-slate-600 dark:text-slate-300 min-h-screen selection:bg-primary-100 dark:selection:bg-primary-900/50 selection:text-primary-700 dark:selection:text-primary-300 relative transition-colors duration-200 overflow-x-hidden">
      
      <!-- Global Background Dynamism: Removido do DOM no Login/Modal via @if para liberar 100% da GPU no Chrome -->
      @if (context.currentUserRole() && !context.isRestoringSession() && !context.isModalOpen()) {
          <app-background-aura />
      }

      <app-toast />
      <app-session-timeout-modal />
      
      @if (context.isRestoringSession()) {
          <app-loading-overlay />
      } @else if (!context.currentUserRole()) {
          @if (context.isPublicStandalone()) {
              <app-public-search />
          } @else {
              <app-login />
          }
      } @else {
          <!-- Main Application Shell -->
          <div class="min-h-screen flex flex-col">
              <!-- Top Header (Logged In) -->
              <header class="sticky top-0 z-50 bg-white/97 dark:bg-slate-900/97 border-b border-slate-200/60 dark:border-slate-800/60 shadow-sm transition-colors duration-300">
                  <div class="container mx-auto px-6 h-20 flex items-center justify-between">
                     <div class="flex items-center gap-6">
                        <!-- Branding / Icon -->
                        <div class="relative group logo-container">
                           <div class="absolute -inset-2 bg-primary-400/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-xl"></div>
                           <div class="relative w-11 h-11 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center shadow-xl shadow-primary-900/5 ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
                              <img [src]="context.currentMunicipality()?.logo_url || 'assets/images/logo-icon.svg'" 
                                   (error)="$any($event.target).src = 'assets/images/logo-icon.svg'"
                                   alt="Logo" class="w-8 h-8 object-contain logo-header"
                                   style="width: 32px; height: 32px">
                           </div>
                        </div>

                        <!-- Divider -->
                        <div class="h-10 w-px bg-slate-200/80 dark:bg-slate-800/80"></div>

                         <!-- User Profile Info -->
                         @if (context.currentUserRole() === 'PACIENTE') {
                            <div class="flex flex-col justify-center text-left px-3 py-1 rounded-xl">
                               <div class="flex items-center gap-2">
                                  <span class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1">
                                     {{ context.currentOperator()?.name || 'Sistema Central' }}
                                  </span>
                               </div>
                               <h1 class="text-sm font-bold text-slate-800 uppercase tracking-tight flex items-center gap-2 leading-none">
                                  {{ normalizeRole(context.currentUserRole()) }}
                                  @if (context.currentMunicipality()) {
                                     <span class="text-primary-600 font-black inline-flex items-center px-2 py-0.5 rounded-md bg-primary-50 text-[10px] uppercase tracking-wider">
                                        {{ context.currentMunicipality().name }}
                                     </span>
                                  }
                               </h1>
                            </div>
                         } @else {
                             <button (click)="activeTab = 'profile'" class="flex flex-col justify-center text-left hover:bg-slate-50 px-3 py-1 rounded-xl transition-all group/profile btn-profile-tab">
                                  <div class="flex items-center gap-2">
                                     <span class="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] leading-none mb-1 group-hover/profile:text-primary-500 transition-colors">
                                        {{ context.currentOperator()?.name || 'Sistema Central' }}
                                     </span>
                                  </div>
                                  <h1 class="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-tight flex items-center gap-2 leading-none">
                                     {{ normalizeRole(context.currentUserRole()) }}
                                     @if (context.currentUserRole() === 'REGULADOR_UBS' && getRegulatorUbsName()) {
                                        <span class="text-primary-600 dark:text-primary-400 font-black inline-flex items-center px-2 py-0.5 rounded-md bg-transparent text-[14px] uppercase tracking-tighter">
                                           &bull; {{ getRegulatorUbsName() }}
                                        </span>
                                     }
                                     @if (context.currentMunicipality()) {
                                        <span class="text-primary-600 dark:text-primary-400 font-black inline-flex items-center px-2 py-0.5 rounded-md bg-primary-50 dark:bg-primary-900/30 text-[10px] uppercase tracking-wider">
                                           {{ context.currentMunicipality().name }}
                                        </span>
                                     }
                                  </h1>
                            </button>
                         }
                     </div>

                     <div class="flex items-center gap-4">
                        <button *ngIf="activeTab === 'profile'" (click)="activeTab = 'dashboard'" 
                           class="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-primary-600 uppercase tracking-widest mr-2">
                           Voltar
                        </button>
                        
                        <!-- Theme Toggle Button -->
                        <button (click)="themeService.toggleDarkMode()" class="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-200 dark:hover:border-primary-800 transition-all shadow-sm">
                           @if (!themeService.isDarkMode()) {
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path></svg>
                           } @else {
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                           }
                        </button>

                        <button (click)="context.logout()" class="group flex items-center gap-3 pl-4 pr-2 py-2 rounded-2xl hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all duration-300 hover:shadow-md hover:shadow-rose-500/20">
                           <span class="text-[11px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest hidden sm:block">Sair</span>
                           <div class="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-rose-100 dark:border-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400 group-hover:bg-rose-600 dark:group-hover:bg-rose-500 group-hover:border-rose-600 group-hover:text-white transition-all shadow-sm">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                           </div>
                        </button>
                     </div>
                  </div>
              </header>

              <main class="flex-grow container mx-auto px-4" [class.py-8]="context.isInSelectionMode() || context.currentUserRole() !== 'ADMIN_GERAL'" [class.pb-12]="!context.isInSelectionMode() && context.currentUserRole() === 'ADMIN_GERAL'">
                  <!-- Global Context Header for Admin Geral (Return to selection) -->
                  @if (context.currentUserRole() === 'ADMIN_GERAL' && !context.isInSelectionMode() && activeTab !== 'profile') {
                      <div class="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 sticky top-20 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md py-5 px-8 rounded-b-[2rem] border-x border-b border-primary-100 dark:border-slate-800 shadow-2xl shadow-primary-900/10 dark:shadow-none animate-enter -mx-4">
                          <div class="flex items-center gap-5">
                              <div class="w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner font-black text-2xl bg-primary-600 text-white">
                                  {{ context.currentMunicipality()?.name?.charAt(0) || 'A' }}
                              </div>
                              <div>
                                  <h2 class="text-xl font-black text-slate-800 dark:text-slate-100 leading-tight">
                                      {{ context.currentMunicipality()?.name || 'Administração Central' }}
                                  </h2>
                                  <p class="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Unidade Gestora Ativa</p>
                              </div>
                          </div>

                          <button (click)="context.backToSelection()" 
                              class="flex items-center gap-3 px-8 py-4 bg-primary-50 dark:bg-slate-800 hover:bg-primary-600 dark:hover:bg-primary-500 text-primary-700 dark:text-primary-400 hover:text-white dark:hover:text-white rounded-[20px] font-black text-xs transition-all border border-primary-100 dark:border-slate-700 hover:border-primary-600 dark:hover:border-primary-500 active:scale-95 group shadow-sm hover:shadow-lg hover:shadow-primary-600/50 dark:hover:shadow-primary-500/50">
                              <svg class="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                              </svg>
                              <span>ALTERAR UNIDADE GESTORA</span>
                          </button>
                      </div>
                  }

                   @if (activeTab === 'profile') {
                           @defer (when activeTab === 'profile') {
                              <app-profile-tab />
                           } @placeholder {
                              <div class="flex items-center justify-center p-12">
                                 <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                              </div>
                           }
                     } @else {
                        @switch (context.currentUserRole()) {
                           @case ('ADMIN_GERAL') { 
                               @defer (when context.currentUserRole() === 'ADMIN_GERAL') {
                                  @if (context.currentMunicipalityId() !== 'GLOBAL' && context.currentMunicipality() && !context.isInSelectionMode()) {
                                          <app-municipality-panel class="block" />
                                  } @else {
                                          <app-admin-panel class="block" />
                                  }
                               } @placeholder {
                                  <div class="flex items-center justify-center p-12">
                                     <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                  </div>
                               }
                           }
                           @case ('ADMIN_MUNICIPAL') { 
                               @defer (when context.currentUserRole() === 'ADMIN_MUNICIPAL') {
                                  <app-municipality-panel class="block" />
                               } @placeholder {
                                  <div class="flex items-center justify-center p-12">
                                     <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                  </div>
                               }
                           }
                           @case ('OPERADOR') { 
                               @defer (when context.currentUserRole() === 'OPERADOR' || context.currentUserRole() === 'OPERADOR_MUNICIPAL') {
                                  <app-municipality-panel class="block" />
                               } @placeholder {
                                  <div class="flex items-center justify-center p-12">
                                     <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                  </div>
                               }
                           }
                           @case ('OPERADOR_MUNICIPAL') { 
                               @defer (when context.currentUserRole() === 'OPERADOR' || context.currentUserRole() === 'OPERADOR_MUNICIPAL') {
                                  <app-municipality-panel class="block" />
                               } @placeholder {
                                  <div class="flex items-center justify-center p-12">
                                     <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                  </div>
                               }
                           }
                           @case ('REGULADOR_UBS') { 
                               @defer (when context.currentUserRole() === 'REGULADOR_UBS') {
                                  <app-operator-panel class="block" />
                               } @placeholder {
                                  <div class="flex items-center justify-center p-12">
                                     <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                  </div>
                               }
                           }
                            @case ('ACS') { 
                                @defer (when context.currentUserRole() === 'ACS') {
                                   <app-acs-dashboard class="block" />
                                } @placeholder {
                                   <div class="flex items-center justify-center p-12">
                                      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                   </div>
                                }
                            }

                           @case ('PACIENTE') { 
                               @defer (when context.currentUserRole() === 'PACIENTE') {
                                  <app-patient-view class="block" />
                               } @placeholder {
                                  <div class="flex items-center justify-center p-12">
                                     <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                  </div>
                               }
                           }
                        }
                     }
              </main>

              <!-- Simple Footer -->
              <footer class="py-6 text-center text-[10px] text-slate-400 dark:text-slate-600 border-t border-slate-100 dark:border-slate-800 mt-auto bg-white/50 dark:bg-slate-900">
                <p>&copy; 2026 Sistema de Regulação FilaSaúde©. <span class="font-bold ml-1 opacity-80">v{{version}}</span></p>
                <div class="mt-2 space-x-4">
                    <button (click)="openLegal(legalTexts.PRIVACY_POLICY)" class="hover:text-primary-600 hover:underline transition-colors">Política de Privacidade</button>
                    <span>&bull;</span>
                    <button (click)="openLegal(legalTexts.TERMS_OF_USE)" class="hover:text-primary-600 hover:underline transition-colors">Termos de Uso</button>
                </div>
              </footer>

             <!-- Global Legal Modal -->
             @if (showLegalModal()) {
                <div class="fixed inset-0 z-[200] flex items-center justify-center p-4">
                   <div class="absolute inset-0 bg-black/60 backdrop-blur-md animate-fade-in" (click)="showLegalModal.set(false)"></div>
                   <div class="relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-[40px] shadow-[0_32px_120px_-20px_rgba(0,0,0,0.4)] w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh] animate-enter border border-white/40 dark:border-slate-800/40">
                      
                      <!-- Modal Header -->
                      <div class="px-10 py-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                         <div class="flex items-center gap-5">
                            <div class="w-12 h-12 rounded-2xl bg-slate-800 dark:bg-brand-main flex items-center justify-center text-white shadow-xl shadow-slate-800/20">
                               <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                               </svg>
                            </div>
                            <div>
                               <h3 class="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">Informação Legal</h3>
                               <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Documentação e Transparência</p>
                            </div>
                         </div>
                         <button (click)="showLegalModal.set(false)" class="p-3 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-2xl transition-all active:scale-95 group">
                            <svg class="w-6 h-6 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                         </button>
                      </div>

                      <!-- Modal Content -->
                      <div class="flex-grow overflow-y-auto custom-scrollbar bg-white dark:bg-slate-900 p-10">
                         <div class="max-w-none prose prose-slate dark:prose-invert prose-headings:font-black prose-headings:tracking-tight prose-p:text-slate-600 dark:prose-p:text-slate-400 prose-p:leading-relaxed prose-strong:text-slate-800 dark:prose-strong:text-slate-200 prose-sm font-medium">
                            <div class="whitespace-pre-wrap text-[13px] text-slate-600 dark:text-slate-400" [innerHTML]="legalContent()"></div>
                         </div>
                      </div>

                      <!-- Modal Footer -->
                      <div class="px-10 py-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/80 backdrop-blur-md flex justify-end items-center gap-4">
                         <span class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden sm:block">FilaSaúde© &bull; 2024</span>
                         <button (click)="showLegalModal.set(false)" 
                            class="px-10 py-4 bg-slate-800 dark:bg-primary-600 hover:bg-slate-900 dark:hover:bg-primary-500 text-white rounded-[20px] text-xs font-black uppercase tracking-widest shadow-2xl shadow-slate-900/20 active:scale-95 transition-all">
                            Entendido
                         </button>
                      </div>
                   </div>
                </div>
             }

          </div>
      }
    </div>
  `,
   styles: [`
    @keyframes header-reveal {
      0% { opacity: 0; transform: translateY(-1rem); }
      100% { opacity: 1; transform: translateY(0); }
    }
    header {
      animation: header-reveal 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
      box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.04);
    }
    @keyframes progress-flow {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(250%); }
    }
    .animate-progress-flow {
      animation: progress-flow 1.5s infinite linear;
    }
  `]
})
export class AppComponent {
   context = inject(ContextService);
   db = inject(DatabaseService);
   loadingService = inject(LoadingService);
   themeService = inject(ThemeService);
   version = environment.version;

   private reloadCount = 0;
   private maxReloads = 3;
   private reloadTimeLimit = 60000; // 1 minute

   legalTexts = LEGAL_TEXTS;
   showLegalModal = signal(false);
   legalContent = signal('');

   openLegal(content: string) {
      this.legalContent.set(content);
      this.showLegalModal.set(true);
   }

   constructor() {
      // Handle direct link to standalone portal via URL parameter (?portal=public) or route (/portal)
      const params = new URLSearchParams(window.location.search);
      const isPortalRoute = window.location.pathname.endsWith('/portal');

      if (params.get('portal') === 'public' || params.get('portal') === 'standalone' || params.get('busca') === 'standalone' || isPortalRoute) {
         this.context.isPublicStandalone.set(true);
      }

      // Restore session on app boot
      this.context.restoreSession().then(() => {
         console.log('[AppComponent] Init: Session check complete.');
      });

      // Recarrega automaticamente quando uma nova versão está disponível
      const swUpdate = inject(SwUpdate);
      if (swUpdate.isEnabled) {
         // Verificar atualizações periodicamente (a cada 1 minuto)
         const checkInterval = setInterval(() => {
            swUpdate.checkForUpdate().then(() => {
               console.log('[AppComponent] Verificação de atualização realizada.');
            });
         }, 60000);

         // Verificar quando a janela volta ao foco
         window.addEventListener('focus', () => {
            swUpdate.checkForUpdate();
         });

         swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe(() => {
               console.log('[AppComponent] Nova versão detectada, preparando atualização...');

               // Proteção básica contra loop de reload sequencial
               const lastReload = localStorage.getItem('last_sw_reload');
               const now = Date.now();

               if (lastReload && (now - parseInt(lastReload)) < 5000) {
                  console.warn('[AppComponent] Reload sequencial detectado. Abortando auto-reload.');
                  return;
               }

               localStorage.setItem('last_sw_reload', now.toString());
               swUpdate.activateUpdate().then(() => {
                  // Se o usuário estiver na tela de login e já começou a digitar, postergamos o reload
                  // para não apagar os dados dele. O reload acontecerá no próximo boot ou se ele clicar em sair.
                  if (!this.context.currentUserRole() && this.context.isUserTyping()) {
                     console.log('[AppComponent] Usuário está digitando no login. Posterga reload.');
                     return;
                  }

                  console.log('[AppComponent] Atualização ativada, recarregando...');
                  setTimeout(() => document.location.reload(), 100);
               });
            });

         // Verificar imediatamente no boot
         swUpdate.checkForUpdate();
      }
   }

   activeTab = 'dashboard'; // 'dashboard' or 'profile'

   normalizeRole(role: string | null) {
      if (!role) return '';
      switch (role) {
         case 'ADMIN_GERAL': return 'Administrador Central';
         case 'ADMIN_MUNICIPAL': return 'Administrador Municipal';
         case 'OPERADOR':
         case 'OPERADOR_MUNICIPAL': return 'Regulador Municipal';
         case 'REGULADOR_UBS': return 'Regulador de Unidade';
         case 'ACS': return 'Agente de Saúde';
         case 'PACIENTE': return 'Cidadão';
         default: return role;
      }
   }

   getRegulatorUbsName() {
      const op = this.context.currentOperator();
      if (!op || !op.location_id) return null;
      const loc = this.db.locationsTable().find(l => l.id === String(op.location_id));
      return loc?.name || null;
   }
}
